import {
  getRawMaterialPlankInfo,
  loadFullPlankManageData,
} from '@/apis/baseMaterial'
import { translateLang } from '@/data/i18n/translateLang'
import {
  IBigPlank,
  IPanelClassification,
  PartType,
  PointType,
} from '@/partTypes'
import store from '@/store'
import { PreferencesSetting } from '@/store/modules/preferencesSetting/types'
import {
  changeLayoutData,
  dealFrameDoorSplice,
  isSawPaiban,
  layoutStart,
  rolloverPlank,
  rotateFrontEdge,
} from '@/util/LayoutFuncs'
import {
  isOnlyProcessSlotAndHole,
  rotate180Part,
  rotatePart,
} from '@/util/LayoutTool'
import {
  isBrandIdentityCompatible,
  isBrandRecord,
} from '@/util/brandPlankIdentity'
import { matchMixPaibanPlan } from '@/util/common'
import { translate } from '@/util/commonFun'
import { compare } from '@/util/commonFuncs'
import {
  dealLayoutResultData,
  dealPlankPoly,
  genPaibanRequestParams,
  getPlateKnifeDiameter,
} from '@/util/dealPaibanData'
import { calcBigpartArea } from '@/util/drawPlankFuncs'
import { compareNumbers } from '@/util/graphCalc'
import {
  compositeTexturePlanks,
  isVirtualCompositePlank,
} from '@/util/plankAlignTexture'
import {
  basisSettingExpandPlank,
  dealMorePlateKnife,
} from '@/util/plankCommonFuncs'
import { getMatCodeByHighPlankProp } from '@/util/plankCommonFuncs'
import { getOriPartByLayout } from '@/util/plankCommonFuncs'
import type { INewMaterialListSpecialPlankType } from '@/views/materialsList/types/material'
import { Modal } from 'ant-design-vue'
import { invert, sortBy } from 'lodash-es'
import { nanoid } from 'nanoid'
import Vue from 'vue'

/**
 *  计算匹配原片使用
 * @param parts 选中的小板
 * @param process_line_id 当前生产线
 * @param errCallback 错误的回调函数
 * @returns
 */
export async function calcBaseMaterial(
  parts: any[],
  selectStandardPlank: any,
  process_line_id: number,
  errCallback: (errType?: string, errText?: string) => any
) {
  const from = {}
  if (window.sessionStorage.getItem('thinkerx_material')) {
    Object.assign(from, { production_from: 'guimen' })
  }
  /** 获取原片数据的接口返回值（含品牌大板聚合数据） */
  const materialRes = await loadFullPlankManageData({
    uid: store.state.userInfo.id,
    ...from,
  })

  // 已经勾选的混排方案
  const selectedMixPaibanSettings =
    store.state.materialListStore!.selectedMixPaibanPlans

  // 没有原片数据直接返回
  if (materialRes.status !== 1) {
    // this.isShowLoading = false
    return false
  } else {
    function getDefalutFromList() {
      let plank
      const selectStandardPlankId = selectStandardPlank.standard_plank_id
      // 兼容默认的原片的id为0
      if (selectStandardPlankId || selectStandardPlankId === 0) {
        plank = materialRes.data.defaultPlankInfo.find(
          (p: any) => p.standard_plank_id == selectStandardPlankId
        )
      } else {
        plank = materialRes.data.defaultPlankInfo.find(
          (i: any) => i.default_selected
        )
      }
      return plank
    }
    const newStandardPlank = Array.isArray(materialRes.data.defaultPlankInfo)
      ? getDefalutFromList()
      : materialRes.data.defaultPlankInfo
    if (newStandardPlank) {
      Object.assign(selectStandardPlank, newStandardPlank)
      store.commit('setSelectStandardPlank', selectStandardPlank)
    }
  }
  const ncSettingRes = { data: store.state.ncSetting, status: 1 }
  // 获取失败直接返回
  if (ncSettingRes.status !== 1) {
    // this.isShowLoading = false
    if (ncSettingRes.data.err_info) {
      errCallback('setting', ncSettingRes.data.err_info)
    } else {
      errCallback('plank', translate('materialPage.noKnife'))
    }
    return
  }
  /** nc设置 */
  const ncSetting = ncSettingRes.data

  /** 判断是否没错误 */
  const errCB = (errType?: string, errText?: string) => {
    errCallback(errType, errText)
  }
  const noError =
    materialRes.data && judgePlateKnife(parts, ncSetting.plate_knife_map, errCB)
  // 有错直接返回
  if (!noError) {
    // this.isShowLoading = false
    return false
  }
  // 检测五六面钻模板是否太老 老版本直接返回
  if (oldFiveSixXYTip(ncSetting, errCB)) {
    // this.isShowLoading = false
    return false
  }
  /** 所选标准大板修边值 */
  const edge = selectStandardPlank.plankEdgeOff
  // 原片信息
  const { isAutoSelected, specialPlankInfo } = materialRes.data
  /** 原片数据数组 */
  const materialResData = JSON.parse(JSON.stringify(materialRes.data))
  /** 所选标准大板宽度 */
  const plankWidth = selectStandardPlank.plankWidth
  /** 所选大板长度 */
  const plankHeight = selectStandardPlank.plankHeight
  /** 大板允许的排版宽 */
  let useDefaultPlankWidth = plankWidth
  /** 大板允许的排版长 */
  let useDefaultPlankHeight = plankHeight
  // 重新聚类超大板
  const specialPlank: any = {}
  // 特殊大板信息
  specialPlankInfo.forEach((plank: any) => {
    // plank.uniqueId = nanoid()
    const { color, thick, matCode } = plank
    // 获取分材质下料刀的指定刀径大小
    const diameter = +getPlateKnifeDiameter(
      `${matCode}:${thick}:${color}`,
      ncSetting
    )
    let widthTrimEdge
    let heightTrimEdge
    if (plank.trim_edge.toString().includes(',')) {
      // 四边修边顺序是 前,后,左,右
      const trimEdgeArr = plank.trim_edge.toString().split(',')
      widthTrimEdge = Number(trimEdgeArr[2]) + Number(trimEdgeArr[3])
      heightTrimEdge = Number(trimEdgeArr[0]) + Number(trimEdgeArr[1])
    } else {
      widthTrimEdge = Number(plank.trim_edge) * 2
      heightTrimEdge = Number(plank.trim_edge) * 2
    }
    // 切割宽度
    plank.useJudgeWidth = plank.width - widthTrimEdge + diameter
    // 切割长度
    plank.useJudgeHight = plank.height - heightTrimEdge + diameter

    const specialHighPlank = matCode.includes('高光_')
    /** 同typeSymbol */
    const symbol = `${matCode.replace(
      '高光_',
      ''
    )}:${thick}:${color}:${specialHighPlank}`
    plank.symbol = symbol
    if (Reflect.has(specialPlank, symbol)) {
      specialPlank[symbol].push(plank)
    } else {
      specialPlank[symbol] = [plank]
    }
  })

  // 根据宽高最长的重新排序
  Object.keys(specialPlank).forEach((k) => {
    specialPlank[k] = specialPlank[k].sort((a: any, b: any) => {
      const aMax = Math.max(a.width, a.height)
      const bMax = Math.max(b.width, b.height)
      return aMax - bMax
    })
  })
  // 已匹配上的原片
  const needMaterialList: any[] = []
  // 尺寸不能匹配的板件
  const notMatchPartList = []
  // 提前对板件进行扩张操作，便于对纹处理，后续再还原板件 rect
  const expandRectPartsMap = calcExpandRectMap(parts, ncSetting)
  parts = compositeTexturePlanks(
    parts,
    ncSetting.panelSize?.layoutGap ?? 0,
    undefined,
    undefined,
    ncSetting?.decimal ?? 2
  )
  for (const part of parts) {
    const { texDir, texture, matCode, thick, oriTexture } = part
    // 用 part.rect 是因为前面扩过了
    const rect = { ...part.rect }
    let expandPart = expandRectPartsMap.get(part.partUniqueId)!
    // 所有小板恢复原有rect 大小 避免数据污染
    if (!isVirtualCompositePlank(part)) {
      part.rect = expandPart!.origRect as any
    } else {
      part._virtualCompositePlanks?.forEach((p: PartType) => {
        const _expandPart = expandRectPartsMap.get(p.partUniqueId)!
        // 还原
        p.rect = _expandPart.origRect as any
        // 对纹虚拟板不需要走额外的玻璃切割机逻辑，小板本身已经扩过了
        _expandPart.contourSizeRecoup = 0
        _expandPart.finalExtendDisMap = null
        // 对纹虚拟组合板只能用内部小板的信息代替，因为虚拟板没有信息
        expandPart = _expandPart!
      })
    }
    let { diameter } = expandPart
    const { contourSizeRecoup, finalExtendDisMap, expandedX, expandedY } =
      expandPart
    diameter = +expandPart?.diameter
    // 计算公式：板件宽度 - 2倍修边 + 刀径 = 板件实际排版宽度
    useDefaultPlankWidth = plankWidth - edge * 2 + diameter
    useDefaultPlankHeight = plankHeight - edge * 2 + diameter
    part.originWidth = rect.width + edge * 2
    part.originHeight = rect.height + edge * 2

    // 根据板件材质，厚度，颜色，高光板来生成板件的唯一标识 lx
    const highPlank = part.is_high_gloss_plank
    const matCodeTemp = matCode.replace('高光_', '')

    // 判断当前小板是否符合选择的混排方案
    const mixPaibanPlan = selectedMixPaibanSettings.find((plan) =>
      matchMixPaibanPlan(plan, part)
    )
    // 如果开启了混排方案，则需要取到混排方案的目标颜色
    const targetTexture = mixPaibanPlan?.targetTexture ?? texture

    // 获取所有需要生成symbol的颜色列表
    const colorList = mixPaibanPlan?.allColors || [texture]

    //  增加匹配规则，需要加入定义颜色的规则
    // 定义symbol模板：包含颜色的位置使用函数，不包含颜色的位置使用字符串
    const symbolTemplates: Array<((color: string) => string) | string> = [
      part.symbol, // 位置0: part.symbol
      (color: string) => `${matCodeTemp}:${thick}:${color}:${highPlank}`, // 位置1: 包含颜色
      (color: string) => `${matCodeTemp}::${color}:${highPlank}`, // 位置2: 包含颜色
      `${matCodeTemp}:${thick}::${highPlank}`, // 位置3: 不包含颜色
      (color: string) => `:${thick}:${color}:${highPlank}`, // 位置4: 包含颜色
      `${matCodeTemp}:::${highPlank}`, // 位置5: 不包含颜色
      (color: string) => `::${color}:${highPlank}`, // 位置6: 包含颜色
      `:${thick}::${highPlank}`, // 位置7: 不包含颜色
      `:::${highPlank}`, // 位置8: 不包含颜色
    ]

    // 按照symbolArr的顺序生成，包含颜色的位置为所有颜色生成symbol
    const symbolArr = symbolTemplates.flatMap((template) =>
      typeof template === 'function' ? colorList.map(template) : template
    )

    // 检查当前板件是否已经有匹配的原片,已存在匹配则跳过
    // needMaterial是已经匹配上的原片
    const isExists = needMaterialList.find((plank) => {
      const copyPartRect = checkMPartRect(JSON.parse(JSON.stringify(part)))
      return (
        symbolArr.includes(plank.symbol) &&
        copyPartRect.width <= plank.useJudgeWidth &&
        copyPartRect.height <= plank.useJudgeHight
      )
    })
    if (isExists) continue
    // 是否需要再进行更大原片的匹配
    let isNeedMatchBiggerPlank = false
    // 是否有默认选用
    let flag = false
    let defaultPlank
    // 是否符号规则的特殊板材都未默认选用
    const notDefaultPlank = symbolArr
      .map((item) => specialPlank[item])
      .filter((item) => item)
      .flat(1)
      .every((plank) => !plank.isPicked)
    for (let i = 0; i < symbolArr.length; i++) {
      if (specialPlank[symbolArr[i]]) {
        defaultPlank = specialPlank[symbolArr[i]]?.find(
          (plank: any) => plank.isPicked
        )
        // 存在默认选用的板件
        if (defaultPlank) {
          // 长宽匹配则使用并且跳过，不匹配则看是否开启更大板件排版
          if (
            checkPlankIsAvailable(
              texDir,
              rect,
              {
                width: defaultPlank.useJudgeWidth,
                height: defaultPlank.useJudgeHight,
              },
              // 此处参数传错了，文鑫分支上修改了，
              symbolArr[i] as unknown as PartType
            )
          ) {
            // 由于混排方案的需要，此处需要取到特殊大板本身的颜色。
            const color = defaultPlank.color || targetTexture
            const newMatCode = getMatCodeByHighPlankProp(highPlank, matCode)
            needMaterialList.push({
              ...defaultPlank,
              matCode: newMatCode,
              // matCode: highPlank ? matCode : matCode.replace('高光_', ''),
              thick,
              color,
              // 每添加一块特殊大板，需要完善symbol
              symbol: `${newMatCode}:${thick}:${color}:${defaultPlank.symbol
                .split(':')
                .pop()}`,
            })
            flag = true
            // 存在默认选用的大板，关闭超尺小板只排特殊大板
            materialRes.data.only_layout_too_big = false
            break
          }
        }
      }
    }

    /** 获取原边旋转处理后的小板 rect */
    function checkMPartRect(copyPart: any) {
      /** 是否需要判断进行原边旋转 */
      const isNeedMRotate =
        store.state.ncSetting.sawSetting.mentaoji_enable &&
        store.state.ncSetting.sawSetting.processType === 'weldingPlank' &&
        store.state.ncSetting.sawSetting.sawToLayout &&
        store.state.ncSetting.sawSetting.edgeNoSaw
      if (isNeedMRotate) {
        rotateFrontEdge([copyPart])
      }
      const copyPartRect = isNeedMRotate
        ? {
            width:
              copyPart.rect.width + // 板子宽度
              expandedX * 2 + //长短边放量，异形全部是长边放量
              (finalExtendDisMap
                ? Number(finalExtendDisMap.left) +
                  Number(finalExtendDisMap.right)
                : 0) + //拉直器
              contourSizeRecoup * 2 + // 玻璃切割机的处理
              diameter, //刀直f
            height:
              copyPart.rect.height +
              expandedY * 2 +
              (finalExtendDisMap
                ? Number(finalExtendDisMap.top) +
                  Number(finalExtendDisMap.bottom)
                : 0) +
              contourSizeRecoup * 2 +
              diameter,
          }
        : rect

      return copyPartRect
    }
    //默认选用未匹配成功则看标准板材
    if (!flag) {
      const copyPart = JSON.parse(JSON.stringify(part))
      const copyPartRect = checkMPartRect(copyPart)
      // 符号规则的特殊板材都未默认选用且标准板材长宽匹配才使用标准板材
      if (
        notDefaultPlank &&
        checkPlankIsAvailable(
          texDir,
          copyPartRect,
          {
            width: useDefaultPlankWidth,
            height: useDefaultPlankHeight,
          },
          copyPart
        )
      ) {
        continue
      }
      isNeedMatchBiggerPlank = true
      notMatchPartList.push(part)
    } else {
      continue
    }

    // 当前板件是否有需要使用超长板来排版
    if (!isNeedMatchBiggerPlank || !isAutoSelected || !notDefaultPlank) continue
    // 此处删除上面添加的当前板件
    notMatchPartList.length = notMatchPartList.length - 1
    // 记录是否成功匹配超大板
    let isMatch = false
    for (let i = 0; i < symbolArr.length; i++) {
      if (specialPlank[symbolArr[i]]) {
        for (const plank of specialPlank[symbolArr[i]]) {
          const isPartBrand = isBrandPart(part)
          const isSpecialPlankBrand = isBrandRecord(plank)
          if (
            isPartBrand !== isSpecialPlankBrand ||
            (isPartBrand && !isBrandIdentityCompatible(part, plank))
          ) {
            continue
          }
          const copyPart = JSON.parse(JSON.stringify(part))
          const copyPartRect = checkMPartRect(copyPart)
          if (
            checkPlankIsAvailable(
              texDir,
              copyPartRect,
              {
                width: plank.useJudgeWidth,
                height: plank.useJudgeHight,
              },
              copyPart
            )
          ) {
            const color = plank.color || targetTexture
            const newMatCode = getMatCodeByHighPlankProp(highPlank, matCode)
            needMaterialList.push({
              ...plank,
              thick,
              color,
              matCode: newMatCode,
              // matCode: highPlank ? matCode : matCode.replace('高光_', ''),
              // 每添加一块特殊大板，需要完善symbol
              symbol: `${newMatCode}:${thick}:${color}:${plank.symbol
                .split(':')
                .pop()}`,
            })
            isMatch = true
            break
          }
        }
      }
      if (isMatch) {
        break
      }
    }

    if (!isMatch) {
      notMatchPartList.push(part)
    }
  }
  const setNotMatchPartList: any[] = []
  notMatchPartList.forEach((item) => {
    if (
      setNotMatchPartList.findIndex(
        (setItem) =>
          item.plankID === setItem.plankID &&
          item.partName === setItem.partName &&
          item.ggid === setItem.ggid
      ) === -1
    ) {
      setNotMatchPartList.push(item)
    }
  })
  return {
    setNotMatchPartList,
    autoAddSpecialPlate: materialRes.data.auto_add_special_plate,
    materialRes: materialResData,
    lackBaseMaterials: notMatchPartList,
    needBaseMaterials: [...new Set(needMaterialList)],
  }
}

/**
 * 是否刀具不符合规则
 * @param parts 小板数据
 * @param plate_knife_map 刀具库列表为一个对象
 * 数据格式{22:3:0:{
 *  diameter:6,
 *  error:"",
 *  id:108,
 *  knife:"default",
 *  matCode:"22",
 *  thick:23
 * }}
 * @param errCallback 错误回调
 * @returns boolean 是否符合规则
 */
export function judgePlateKnife(
  parts: any[],
  plate_knife_map: any,
  errCallback?: (errType?: string, errText?: string) => any
) {
  // 没有分材质下料刀直接返回true
  if (!plate_knife_map) return true
  /** 开料清单已选板件或者预选板件 */
  const selectPlanks = parts
  /** 处理板件列表 */
  const planksArr: any[] = []
  selectPlanks.forEach((item) => {
    // typeSymbol是由 matCode:thick:texture:isHighGlass 组成的
    const symbolArr = String(item.typeSymbol).split(':')
    planksArr.push(
      // 但是刀具匹配只需要matCode:thick
      symbolArr.slice(0, -2).join(':')
    )
  })
  /** 刀具列表的key值数组 由matCode:thick组成 样例：多层实木:18.0 */
  let knifesArr = Object.keys(plate_knife_map)
  knifesArr = knifesArr.map((item) => {
    // 如果小数部分为0，则取整数
    if (!Number(item.split('.')[1])) {
      item = item.split('.')[0]
    }
    return item
  })
  if (!intersection(knifesArr, planksArr)) return true
  // 如果开启了激光一体机 则不校验原片刀具
  if (store.state.ncSetting.laser_enable) return true
  let noError = true
  const errorKnifeName: string[] = []

  for (const key in plate_knife_map) {
    if (plate_knife_map[key].error && planksArr.includes(key.split('.')[0])) {
      // 添加逻辑：将匹配上的错误原片刀具名字添加到数组中
      errorKnifeName.push(plate_knife_map[key].knife)
      // noError = false
      // errCallback && errCallback('plank', plate_knife_map[key].error)
      // break
    }
  }
  if (errorKnifeName.length) {
    // 说明有错误刀具则返回false
    const errorMsg = `已开启不同材质下料刀，该生产线下料刀（${errorKnifeName.join(
      '、'
    )}）直径为空，请前往添加对应刀径`
    noError = false
    errCallback && errCallback('plank', errorMsg)
  }
  return noError
}

export function dealDataForPaiban(
  plankList: any[],
  ncSetting: any,
  otherSetting: {
    isSpecialShape: boolean
    isNewLayout: boolean
    baseMaterialList: any[]
    surplusObj?: any
    isOnlyLayoutTooBig?: boolean
  }
) {
  // 多材质不同下料刀
  const moreMatCodePaibanData = dealMorePlateKnife(plankList, ncSetting)
  // prePaibanData进行push操作
  store.commit('setIsSinglePushPrePaibanData', true)
  // 清空perPaibanData
  store.commit('setPrePaibanData', [])
  const fnialList: any[] = []
  // 不同开料刀生成不同的请求数据
  moreMatCodePaibanData.forEach((paibanData) => {
    const {
      preLayoutData,
      setting: { diameter },
    } = paibanData
    const nc = JSON.parse(JSON.stringify(ncSetting))
    if (!nc.laser_enable) {
      // 如果未开启激光一体机，才使用多材质对应刀的直径
      nc.knife.diameter = diameter
    }
    // nc.knife.diameter = diameter
    const frameDoorSpliceData = dealFrameDoorSplice(preLayoutData, diameter)
    changeLayoutData(frameDoorSpliceData)
    // 进行排版前的板件处理
    const val = layoutStart(nc)
    fnialList.push(val)
  })
  store.commit('setIsSinglePushPrePaibanData', false)
  if (store.state.errorPathPlankID.length) {
    Vue.prototype.$message({
      type: 'error',
      message: `板件内部存在异常数据（板号：${store.state.errorPathPlankID}），请检查！`,
    })
    return null
  }
  if (fnialList.some((it) => !it) || !fnialList.length) {
    Vue.prototype.$message({
      type: 'error',
      message:
        '<span id="material_knife_error">未设置下料刀直径, 请前往雕刻机设置刀具!</span>',
      dangerouslyUseHTMLString: true,
    })
    return null
  }
  if (fnialList.length) {
    const newList = fnialList.filter((i) => i)
    const slotErrList = newList.filter((i) => {
      return i && i.layout?.isErr
    })

    if (slotErrList.length) {
      Modal.error({
        title: translateLang('提示'),
        content: slotErrList[0] ? slotErrList[0].layout?.errMsg : '',
        centered: true,
      })
      return null
    }
  }
  const resultArr = []
  let hasErr = false
  for (const val of fnialList) {
    /** 处理上面layoutStart函数所返回的数据用于排版 */
    const data = dealLayoutResultData(
      val,
      otherSetting.baseMaterialList,
      {
        isSpecialShape: otherSetting.isSpecialShape,
        customPlankOrder: store.state.customPlankOrder,
        plankOrderList: store.state.plankOrderList,
      },
      ncSetting,
      store,
      otherSetting.isNewLayout,
      otherSetting.isOnlyLayoutTooBig
    )
    if (!data) {
      hasErr = true
      break
    }
    resultArr.push(data)
  }
  if (hasErr) {
    return null
  }
  let surplusObj = {}
  // 获取余料数据
  if (otherSetting.surplusObj) {
    // 处理余料数据 准备排版数据
    surplusObj = otherSetting.surplusObj
  }
  /** 参数列表 */
  const paramObjArr: any[] = []
  resultArr.forEach(({ result }) => {
    /** 生成排版接口所需参数 */
    const paramObj = genPaibanRequestParams(store, surplusObj, result, 0)
    paramObjArr.push(paramObj)
  })
  return paramObjArr
}

/** 判断两个数组的包含关系 */
export function intersection(arr1: any[], arr2: any[]) {
  return Boolean(arr1.filter((i) => arr2.includes(i)).length)
}

/**
 * 旧版五六面钻设置提示
 * @param res 获取的ncSetting接口返回值
 * @param errCallback 错误的回调
 * @returns 返回boolean值
 */
export function oldFiveSixXYTip(res: any, errCallback: () => any) {
  // 五六面钻设置过老提示 (为啥是499，不明)
  if (res.status == 499) {
    const h = Vue.prototype.$createElement
    Vue.prototype
      .$msgbox({
        title: '警告',
        message: h('p', null, [
          h('i', { style: 'color: red' }, '五六面钻设置过老'),
          h('span', null, '，请联系售后人员重新对接'),
        ]),
        confirmButtonText: '确定',
      })
      .catch((e: any) => {
        throw e
      })
    errCallback && errCallback()
    return true
  } else {
    return false
  }
}

/**
 * 检查大板是否可用
 * @param texDir 小板纹理
 * @param partInfo 小板数据
 * @param plankInfo 大板数据
 * @returns 返回boolean值
 */
export function checkPlankIsAvailable(
  texDir: string,
  partInfo: any,
  plankInfo: any,
  part?: PartType
) {
  const isSawCutOnlyWeld = store.state.ncSetting.sawSetting.sawCutOnlyWeld
  // 横竖纹的判断 小板宽小于等于大板宽、小板长小于等于大板长
  const isEnableRoate =
    store.state.ncSetting.sawSetting.mentaoji_enable &&
    store.state.ncSetting.sawSetting.sawToLayout &&
    store.state.ncSetting.sawSetting.processType === 'weldingPlank' &&
    isSawPaiban(store) &&
    (!isSawCutOnlyWeld || (isSawCutOnlyWeld && part?.isWeldingPlank))

  // 无纹理可以随便转，但是锯片又不能旋转小板
  if (texDir !== 'notcare' || isEnableRoate) {
    return (
      partInfo.width <= plankInfo.width && partInfo.height <= plankInfo.height
    )
  } else {
    // 无纹理的判断 即小板矩形短边小于等于大板短边、小板长边小于等于大板长边同时满足
    const [partMin, partMax] = [partInfo.width, partInfo.height].sort(
      (a, b) => a - b
    )
    const [plankMin, plankMax] = [plankInfo.width, plankInfo.height].sort(
      (a, b) => a - b
    )
    return partMin <= plankMin && partMax <= plankMax
  }
}

export async function dealStandardPlank(
  selectStandardPlank: any,
  params: { uid: number; from?: 'guimen' }
) {
  if (selectStandardPlank) {
    return { ...selectStandardPlank, plankEdgeOff: selectStandardPlank.margin }
  } else {
    const res = await getRawMaterialPlankInfo(params)
    let defaultStandardPlank = null
    if (res.status) {
      defaultStandardPlank = Array.isArray(res.data.defaultPlankInfo)
        ? res.data.defaultPlankInfo.find((plank: any) => plank.default_selected)
        : res.data.defaultPlankInfo
    }
    return defaultStandardPlank
  }
}

/** 组装排版页面需要使用的 finalDrawData 数据 */
/** 其他分支有写公共文件的判断方法，到时候上线时替换 */
/**
 * @description 是否是余料大板，也可以用于判断板件是否在一个余料大板上
 * @param plank 板件
 * @returns
 */
export function isSurplusBigPlank(
  plank: Partial<
    Pick<PartType & IBigPlank, 'surplusInfo' | 'specialType' | 'otherPlate'>
  >
) {
  // 判断逻辑: surplusInfo有值且surplusInfo.other_plate为 false
  return Boolean(
    ((Reflect.has(plank, 'surplusInfo') &&
      Object.keys(plank?.surplusInfo ?? {}).length) ||
      (Reflect.has(plank, 'specialType') && plank.specialType === 'surplus')) &&
      !plank.surplusInfo?.other_plate
  )
}
/**
 * @description 是否是余料小板
 * @param plank 板件
 * @returns
 */
export function isSurplusPlank(
  plank: Partial<
    Pick<PartType & IBigPlank, 'surplusInfo' | 'specialType' | 'otherPlate'>
  >
) {
  return plank.specialType === 'surplus' || plank.specialType === 'supplus'
}

/** 是否计入排版小板数量（排除蓝色余料与余料小板） */
export function isNormalPlankPart(
  part: Partial<Pick<PartType, 'specialType'>> & { blueAreaSurplus?: boolean }
) {
  return !part?.blueAreaSurplus && !isSurplusPlank(part)
}

/**
 * @description 检查是否是特殊大板
 * @param bigPlank 大板数据
 * @param isStrictCheck 是否严格检查是否是特殊大板(特殊情况下不需要严格检查)
 * @returns
 */
export function isSpecialBigPlank(
  bigPlank: Partial<Pick<IBigPlank, 'otherPlate' | 'surplusInfo'>>,
  isStrictCheck = true
) {
  // 判断逻辑: otherPlate有值且surplusInfo中other_plate是 true 或没有值才是特殊大板
  return (
    Reflect.has(bigPlank, 'otherPlate') &&
    Object.keys(bigPlank?.otherPlate ?? {}).length &&
    (!isStrictCheck || bigPlank.surplusInfo?.other_plate)
  )
}

/**
 * @description 检查是否是单面板
 * @param plank 大板
 * @returns
 */
function isSingleFacePlank(plank: { ableToggle: boolean }) {
  return !plank.ableToggle
}

const plankOrders = [
  { type: 'surplus' }, // 余料
  { type: 'normal' }, // 普通板
  { type: 'big_normal' }, // 普通特殊大板
] as const
type IPlankOrderKey = typeof plankOrders[number]['type']
/**
 * @description 按照设定的优先级进行排序
 * @param paibanData 排版处理好的每个大板信息
 */
export function dealPaibanDataByOrder(paibanData: IBigPlank[]) {
  const plankTypeMap = plankOrders.reduce((map, { type }) => {
    map.set(type, [])
    return map
  }, new Map<IPlankOrderKey, IBigPlank[]>())
  paibanData.forEach((bigPlank) => {
    let key: IPlankOrderKey = 'normal'
    if (isSurplusBigPlank(bigPlank)) {
      key = 'surplus'
    } else if (isSpecialBigPlank(bigPlank)) {
      key = 'big_normal'
    }
    plankTypeMap.get(key)!.push(bigPlank)
  })
  return Array.from(plankTypeMap.values())
}

/**
 *@description 计算是否是单双面板并分离单双面大板
 */
export function separationSingleAndDouble(
  paibanData: IBigPlank[],
  nc: INcSetting
) {
  return paibanData.reduce(
    (pre, cur) => {
      cur.ableToggle = isNeedFanByBigPlank(cur, nc)
      pre[isSingleFacePlank(cur) ? 1 : 0].push(cur)
      return pre
    },
    [[], []] as [IBigPlank[], IBigPlank[]]
  )
}

/**
 * @description 计算大板数组优化率并按照优化率进行降序排序
 * @param paibanData 大板数据
 * @param nc
 * @param isUnuseSurplus 是否计算余料优化率
 * @returns
 */
export function sortPlankDataByOptimizationRate(
  paibanData: IBigPlank[],
  nc: INcSetting,
  isUnuseSurplus: boolean
) {
  reCalcAreaOrRateByPlanks(paibanData, nc, isUnuseSurplus)
  return paibanData.sort((a, b) => b.usedRate - a.usedRate)
}

/** @description 重新计算大板组优化率以及利用率 */
export function reCalcAreaOrRateByPlanks(
  paibanData: IBigPlank[],
  nc: INcSetting,
  isUnuseSurplus: boolean
) {
  paibanData.forEach((bigPlank) => {
    const { usedArea, usedRate } = calcBigpartArea(bigPlank, nc, isUnuseSurplus)
    bigPlank.usedArea = usedArea
    bigPlank.usedRate = usedRate > 1 ? 1 : usedRate
  })
}

/**
 * 根据排版数据按照固定规则进行排序和分类
 * @description 将排版数据按照材质、颜色、厚度等规则进行分类和排序
 * @example
 * 输入数据:
 * paibanData = [{
 *   texture: "白色", // 颜色
 *   matCode: "实木", // 材质
 *   thick: 18, // 厚度
 *   parts: [...], // 板件列表
 *   usedArea: 100, // 使用面积
 *   usedRate: 0.8 // 利用率
 * }]
 *
 * 返回数据:
 * {
 *   data: IPanelClassification,
 *   totalUsedArea: 200, // 总使用面积
 *   totalUsedRate: 0.85 // 总体利用率
 * }
 */
export function genFinalDataOrder(
  paibanData: IBigPlank[],
  nc: INcSetting,
  preference: PreferencesSetting,
  isForcedReset = false,
  options?: {
    /** 之前的finaldrawdata 排序，传入后会按照此参数进行排序，不传入则默认按照之前的规则排序 */
    beforeTitleSorts?: string[]
  }
) {
  // 对于不曾排序排序的数据才排序，排序后的数据按照原有的顺序进行拆分即可
  // _sortNum的作用就是让这里认为板件是否排过序，如果想要重新排序可以删除_sortNum再传入
  const isSorted = paibanData.some((it) => Reflect.has(it, '_sortNum'))
  if (!isSorted || isForcedReset) {
    paibanData = sortPlankDataByOptimizationRate(
      paibanData,
      nc,
      preference.isSurplusNoRoll
    )
    // 按照顺序拆分并全部分离成单双面板
    paibanData = dealPaibanDataByOrder(paibanData).reduce((acc, cur) => {
      acc.push(...separationSingleAndDouble(cur, nc).flat())
      return acc
    }, [] as IBigPlank[])
  }
  const { drawMap } = transformPaibanDataToFinalDrawData(paibanData)
  const normalResult = Array.from(drawMap.normal.values())
  const otherPlate = Array.from(drawMap.otherPlate.values())
  if (!isSorted) {
    // 第一中排序按照厚度/特殊大板宽度进行排序
    normalResult.sort(compare('thick', 'down') as any)
    otherPlate.sort(compare('thick', 'down', 'otherPlateW') as any)
  }
  let data = [...normalResult, ...otherPlate]
  if (!isSorted) {
    // 第二种排序按照材质/大小进行排序
    data = sortDrawDataByTextureOrSize(data)
    if (preference.isSortByPlankCountDesc) {
      data = changeFinalDataByPlankNum(data)
    }
  }
  const totalArea = getPaibanDataTotalArea(paibanData)
  if (options?.beforeTitleSorts?.length) {
    // 第三种排序基本就是手动调整过 根据手动调整的排序 也是目前代码中的最后一层排序规则
    const orderMap = invert(options.beforeTitleSorts)
    data = sortBy(data, (item) => +orderMap[item.title])
  }
  // 如果 parts 里面有 isDesMaterialNoCut: true，给 data 也加上 isDesMaterialNoCut: true
  data.forEach((item) => {
    const hasDesMaterialNoCut = item.data.some((bigPlank) =>
      bigPlank.parts.some((part) => (part as any).isDesMaterialNoCut === true)
    )
    if (hasDesMaterialNoCut) {
      item.isDesMaterialNoCut = true
      item.data.forEach((bigPlank) => {
        bigPlank.isDesMaterialNoCut = true
      })
    }
  })
  // 将 data 中包含 isDesMaterialNoCut 为 true 的大板放到最后
  data.sort((a, b) => {
    // 使用已添加的 isDesMaterialNoCut 字段进行判断
    const aHasDesMaterialNoCut = (a as any).isDesMaterialNoCut === true
    const bHasDesMaterialNoCut = (b as any).isDesMaterialNoCut === true

    // 如果 a 有 isDesMaterialNoCut 为 true 的数据，b 没有，a 排在后面
    if (aHasDesMaterialNoCut && !bHasDesMaterialNoCut) {
      return 1
    }
    // 如果 a 没有，b 有，a 排在前面
    if (!aHasDesMaterialNoCut && bHasDesMaterialNoCut) {
      return -1
    }
    // 其他情况保持原有顺序
    return 0
  })
  if (!isSorted) {
    // 整理复用组顺序，确保父板在前子板在后
    data = sortReuseGroupsWithParentFirst(data)
  }

  return {
    ...totalArea,
    data,
  }
}

/**
 * @description 整理复用组顺序，基于父板在排序后数组中的位置，将子板移动到父板之后
 * @param {IPanelClassification[]} data - 排版数据
 * @returns {IPanelClassification[]} 整理后的数据
 */
function sortReuseGroupsWithParentFirst(
  data: IPanelClassification[]
): IPanelClassification[] {
  return data.map((group) => {
    // 没有复用关系，不需要处理
    const hasReuse = group.data.some((b) => b._isReused || b._reuseParentId)
    if (!hasReuse) return group

    // 拷贝数组，避免修改原数组
    const sortedData = [...group.data]

    // 找到所有父板
    const parentBoards = sortedData.filter((b) => b._isReused)

    for (const parent of parentBoards) {
      const parentId = Array.isArray(parent.bigPlankUniqueId)
        ? parent.bigPlankUniqueId[0]
        : parent.bigPlankUniqueId

      // 找到该父板的所有子板及其索引
      const childEntries: { index: number; board: IBigPlank }[] = []
      sortedData.forEach((board, index) => {
        if (!board._reuseParentId) return
        const childParentId = Array.isArray(board._reuseParentId)
          ? board._reuseParentId[0]
          : board._reuseParentId
        if (childParentId === parentId) {
          childEntries.push({ index, board })
        }
      })

      if (childEntries.length === 0) continue

      // 提取子板
      const children = childEntries.map((e) => e.board)

      // 从后往前删除子板，避免索引偏移
      for (let i = childEntries.length - 1; i >= 0; i--) {
        sortedData.splice(childEntries[i].index, 1)
      }

      // 重新找到父板的索引（删除操作可能改变了索引）
      const parentIndex = sortedData.indexOf(parent)

      // 将子板插入到父板之后
      sortedData.splice(parentIndex + 1, 0, ...children)
    }

    return { ...group, data: sortedData }
  })
}

/** 比较操作符映射 */
export const compareMap: Record<string, (a: number, b: number) => boolean> = {
  gt: (a, b) => a > b,
  lt: (a, b) => a < b,
  eq: (a, b) => a === b,
}

/**
 * @description 传入大板数据是否支持翻版
 * @param bigPlank 大板数据
 */
export function isNeedFanByBigPlank(
  bigPlank: IBigPlank,
  ncSetting: INcSetting
) {
  let toggleFlag = true // true 是不可以翻 false 是可以翻
  let isThroughHole = false
  let isThroughSlot = false
  /** 是否是双面板 */
  let isRollPlank = false
  /** 是否仅计算雕刻机加工的孔槽翻版 */
  const isCalTrunNumAFTshunt = ncSetting.cal_turn_num_aft_shunt
  /** 是否是组合设备 */
  const combinationEquip = ncSetting.combinationEquip
  const onlyHoleSlotArr = bigPlank.parts
    .filter((p) => p.specialType !== 'surplus')
    .map((part) => [part.slots, part.holes, part.handleSlopes ?? []])
    .flat(2)
  if (isCalTrunNumAFTshunt && !combinationEquip) {
    /** 是否异形孔数据在雕刻机上加工 */
    const isEngravingCurveholes = bigPlank.parts.every(
      (part) => part.process_in_engraving_to_curve
    )
    const hasEngravingCurveholeBack = bigPlank.parts.some((part) => {
      return (
        part?.engraving_cut_curve && part.engraving_cut_curve.includes('back')
      )
    })
    /** 所有分流的孔槽 */
    const shuntHoleSlotArr = onlyHoleSlotArr.filter(
      (it) => it.process_in_engraving || it.process_in_engraving_to_turn
    )
    /** 所有的curveHoles */
    const shuntCurveHoles = bigPlank.parts
      .filter((p) => p.specialType !== 'surplus')
      .map((part) => part.curveHoles ?? [])
      .flat(1)
    /** 是否有在雕刻机上反面加工的孔槽 */
    const hasEngravingBackProcess =
      shuntHoleSlotArr.some((it) => it.side == -1 && it.process_in_engraving) ||
      (isEngravingCurveholes &&
        shuntCurveHoles.some((hole) => hole.side == -1)) ||
      hasEngravingCurveholeBack
    // bigPlank.ableToggle = hasEngravingBackProcess
    isRollPlank = hasEngravingBackProcess
    const hasBackHoleSlots = [...onlyHoleSlotArr, ...shuntCurveHoles].some(
      (it) => it.side == -1
    )
    if (!hasEngravingBackProcess && ncSetting.ncHoleSlot != 'doubleHoleSlot') {
      isRollPlank = false
      Object.assign(bigPlank, {
        noEngraving: hasBackHoleSlots, // 用于标识是否需要绘制“雕“字
      })
    } else {
      isRollPlank = hasEngravingBackProcess
      Object.assign(bigPlank, {
        noEngraving: hasEngravingBackProcess ? false : hasBackHoleSlots, // 用于标识是否需要绘制“雕“字
        ableToggle: isRollPlank,
      })
    }

    return isRollPlank
  }

  for (const part of bigPlank.parts) {
    if (isSurplusPlank(part)) continue
    const {
      holes = [],
      slots = [],
      curveHoles = [],
      millInfo = [],
      handleSlopes = [],
      lineSlots = [],
    } = part
    const colletArr = [
      ...(slots ?? []),
      ...(handleSlopes ?? []),
      ...(millInfo ?? []),
    ]

    /** 打穿的异形孔不考虑翻面 */
    const allNoThroughCurveHoles = curveHoles.filter(
      (h) => h.deep + 0.001 < bigPlank.thick
    )

    const allHoleSlotArr = [
      ...colletArr,
      ...allNoThroughCurveHoles,
      ...holes,
      ...lineSlots,
    ]
    // 为 false 就需要翻版
    if (toggleFlag) {
      toggleFlag = allHoleSlotArr.every((v: any) => v.side == 1)
    }
    if (!isThroughHole) {
      let isLockHole = false
      const rules: any[] = []
      // 如果开启翻面两次打穿孔槽，并且开启设置不生效翻面两次打穿规则的孔，则需要过滤掉满足设置不生效翻面两次打穿规则的孔
      if (
        ncSetting.throughTowSideToCut &&
        ncSetting.non_through_slot_two_side_cut?.is_open
      ) {
        // 开启设置不生效翻面两次打穿规则的孔
        const specialHoleSlotSide =
          ncSetting.non_through_slot_two_side_cut?.special_hole_slot_side
        if (specialHoleSlotSide.length) {
          specialHoleSlotSide.forEach((item: any) => {
            if (item.key === 'LockHole') {
              isLockHole = true
            } else {
              rules.push(item.rules)
            }
          })
        }
      }

      // 合并过滤条件：1. 过滤锁孔（如果开启） 2. 过滤满足规则的孔
      const filterHoles = holes.filter((v: any) => {
        // 如果开启锁孔过滤，则过滤掉锁孔类型的孔
        if (isLockHole && v.symbol === 'LockHole') {
          return false
        }
        // 如果没有规则，保留该孔
        if (rules.length === 0) {
          return true
        }
        if (v.symbol !== 'LockHole') {
          // 如果没有开启锁孔，这里会有锁孔情况 常规孔规则要过滤掉锁孔
          // 检查是否满足任何一条规则，如果满足则过滤掉（返回 false）
          const diameter = Number(v.diameter)
          const isMatchRule = rules.some((rule: any) => {
            const ruleValue = Number(rule.value)
            const compareFn = compareMap[rule.compare]
            return compareFn ? compareFn(diameter, ruleValue) : false
          })
          return !isMatchRule
        }
        return true
      })

      if (filterHoles.length > 0) {
        isThroughHole = filterHoles.some(
          (v: any) => v.deep * 1 + 0.001 >= bigPlank.thick && !v.isFixedSide
        )
      }
    }
    if (!isThroughSlot) {
      isThroughSlot = colletArr.some(
        (v: any) => v.deep * 1 + 0.001 >= bigPlank.thick
      )
    }

    // 判断是否存在所有的打穿孔且固定面加工且反面加工
    const allFixedSideHoles =
      holes.length > 0 &&
      holes.every(
        (v: any) =>
          v.deep * 1 + 0.001 >= bigPlank.thick && v.isFixedSide && v.side == -1
      )

    // 判断了通孔/通槽分双面打穿
    // 小板存在反面（side == -1）的孔/槽/异形孔/牛角槽/拉手槽（且不包含三合一预埋孔）
    // 开启通孔双面加工（throughTowSideToCut）且存在打穿孔（深度 ≥ 板厚）(且不包含三合一预埋孔)
    // 开启通槽双面加工（through_slot_two_side_cut）且存在打穿槽（深度 ≥ 板厚）

    const isNeedToggle =
      !toggleFlag ||
      (ncSetting.throughTowSideToCut && isThroughHole) ||
      (ncSetting.through_slot_two_side_cut && isThroughSlot) ||
      allFixedSideHoles

    // 记录是否可以切换正反
    // bigPlank.ableToggle = isNeedToggle
    isRollPlank = isNeedToggle

    // // 条件全部满足跳出循环
    // if (!toggleFlag && isThroughHole && isThroughSlot) break
  }

  return isRollPlank
}

/**
 * @description 改变传入数据的顺序基于内部存在的大板数量
 * @param drawData IPanelClassification
 * @param isAscending 是否升序排列，默认为false(降序)
 */
export function changeFinalDataByPlankNum(
  drawData: IPanelClassification[],
  isAscending = false
) {
  return sortPanelDataByRule(drawData, 'plankNum', isAscending)
}

/**
 * @description 根据指定规则对面板数据进行排序
 * @param data 需要排序的数据，需要是IPanelClassification 类型
 * @param sortRule 排序规则
 * @param isAscending 是否升序排列，默认为false(降序)
 */
export function sortPanelDataByRule(
  data: IPanelClassification[],
  sortRule: 'plankNum' | 'usedRate' | 'usedArea' = 'usedRate',
  isAscending = false
): IPanelClassification[] {
  const sortedData = [...data]
  switch (sortRule) {
    case 'plankNum':
      sortedData.sort((a, b) => {
        const diff = a.counts - b.counts
        return isAscending ? diff : -diff
      })
      break
    case 'usedRate':
      sortedData.sort((a, b) => {
        const diff = a.usedRate - b.usedRate
        return isAscending ? diff : -diff
      })
      break
    case 'usedArea':
      sortedData.sort((a, b) => {
        const diff = a.usedArea - b.usedArea
        return isAscending ? diff : -diff
      })
      break
    default:
      // 默认按利用率排序
      sortedData.sort((a, b) => {
        const diff = a.usedRate - b.usedRate
        return isAscending ? diff : -diff
      })
  }
  return sortedData
}

/**
 * @description 获取当前 paibandata 总优化率以及利用率
 */
export function getPaibanDataTotalArea(paibanData: IBigPlank[]) {
  let totalUsedArea = 0
  let totalUsedRate = 0
  paibanData.forEach((bigPlank) => {
    if (bigPlank.parts.some((part) => (part as any).isDesMaterialNoCut)) return
    totalUsedArea += bigPlank.usedArea
    totalUsedRate += bigPlank.usedRate
  })
  return { totalUsedArea, totalUsedRate }
}

/**
 * @description 将 paibandata 转换为 finaldrawdata
 * @returns { drawMap: { normal: Map<string, IPanelClassification>, otherPlate: Map<string, IPanelClassification> }, finalDrawData: IPanelClassification[] }
 */
export function transformPaibanDataToFinalDrawData(paibanData: IBigPlank[]): {
  drawMap: {
    normal: Map<string, IPanelClassification>
    otherPlate: Map<string, IPanelClassification>
  }
  finalDrawData: IPanelClassification[]
} {
  const drawMap = {
    normal: new Map(),
    otherPlate: new Map(),
  }
  paibanData.forEach((bigPlank, idx) => {
    const {
      matCode,
      thick,
      texture,
      usedArea,
      usedRate,
      otherPlate,
      surplusInfo,
      ableToggle,
      parts,
      brandName,
      supplierName,
      colorId,
    } = bigPlank
    const isSpecial = isSpecialBigPlank(bigPlank, false)
    const curMap = isSpecial ? drawMap.otherPlate : drawMap.normal
    const title = `${bigPlank.texture}:${bigPlank.matCode}:${bigPlank.thick}${
      brandName && supplierName ? `:${brandName}:${supplierName}` : ''
    }${
      isSpecial
        ? `:${bigPlank.otherPlate.width}:${bigPlank.otherPlate.height}`
        : ''
    }`
    bigPlank._sortNum = idx
    bigPlank.sideType = bigPlank.sideType ?? 1

    const obj =
      curMap.get(title) ??
      curMap
        .set(title, {
          title,
          matCode,
          thick,
          texture,
          data: [],
          fanbanCount: 0,
          counts: 0,
          usedRate: 0,
          usedArea: 0,
          otherPlate,
          type: surplusInfo ? 'surplus' : '',
          otherPlateW: isSpecial ? otherPlate?.width : undefined,
          brandName,
          supplierName,
          colorId,
        })
        .get(title)
    obj.isManual = parts.some((v) => v.isManual)
    obj.hasDoor = parts.some((v) => v.type == 'SingleDoor')
    obj.counts += 1
    obj.usedRate += usedRate
    obj.usedArea += usedArea
    obj.fanbanCount += ableToggle ? 1 : 0
    obj.data.push(bigPlank)
  })
  return {
    drawMap,
    finalDrawData: Array.from(drawMap.normal.values()).concat(
      Array.from(drawMap.otherPlate.values())
    ),
  }
}

/**
 * @description 生成drawdata/finaldrawdata 当前的的排序
 * @param drawData
 * @returns
 */
export function genBeforeTitleSorts(drawData: IPanelClassification[]) {
  return drawData.map((item) => item.title)
}

/**
 * @description 按照材质/大小进行排序
 * @param data
 * @returns
 */
export function sortDrawDataByTextureOrSize(data: IPanelClassification[]) {
  const high_plank = []
  const big_high_plank = []
  const normal_plank = []
  const big_normal_plank = []
  for (const d of data) {
    const is_high = d.matCode.includes('高光_')
    if (is_high && d.otherPlate) {
      big_high_plank.push(d)
    } else if (is_high) {
      high_plank.push(d)
    } else if (d.otherPlate) {
      big_normal_plank.push(d)
    } else {
      normal_plank.push(d)
    }
  }
  return [
    ...high_plank,
    ...big_high_plank,
    ...normal_plank,
    ...big_normal_plank,
  ]
}

/**
 * 为仅加工孔槽（不下料）的小板按材质+厚度+颜色分组，
 * 取同组最大宽高各 +200 生成一组虚拟原片，供排版算法使用
 * 此方式仅仅是一个临时处理，所以边界添加了 200，后续还是建议重写整个功能(其实我现在就想重写的，可惜不让我重写)
 */
export function genBaseMaterialsForNotCutParts(
  parts: PartType[],
  processLine: any
): INewMaterialListSpecialPlankType[] {
  // 筛选满足「仅加工孔槽」条件的小板
  const notCutParts = parts.filter((part) =>
    isOnlyProcessSlotAndHole(processLine, part.matCode)
  )
  if (!notCutParts.length) return []

  const EXTRA_SIZE = 200
  // 按 材质:厚度:颜色 分组，记录每组的最大宽高
  const groupMap = new Map<
    string,
    {
      matCode: string
      thick: number
      color: string
      maxW: number
      maxH: number
    }
  >()

  for (const part of notCutParts) {
    const { matCode, thick, texture } = part
    const groupKey = `${matCode}:${thick}:${texture}`
    const w = part.oRect?.width ?? part.rect?.width ?? 0
    const h = part.oRect?.height ?? part.rect?.height ?? 0

    const existing = groupMap.get(groupKey)
    if (existing) {
      existing.maxW = Math.max(existing.maxW, w)
      existing.maxH = Math.max(existing.maxH, h)
    } else {
      groupMap.set(groupKey, {
        matCode,
        thick,
        color: texture,
        maxW: w,
        maxH: h,
      })
    }
  }

  const result: INewMaterialListSpecialPlankType[] = []
  for (const [, group] of groupMap) {
    const width = Math.ceil(group.maxW) + EXTRA_SIZE
    const height = Math.ceil(group.maxH) + EXTRA_SIZE
    const highPlank = group.matCode.includes('高光_')
    const symbol = `${group.matCode}:${group.thick}:${group.color}:${highPlank}`

    result.push({
      id: 0,
      matCode: group.matCode,
      thick: group.thick,
      color: group.color,
      width,
      height,
      isPicked: true,
      trim_edge: 0,
      window_plank_id: '',
      useJudgeWidth: width,
      useJudgeHeight: height,
      symbol,
    })
  }

  return result
}

/**
 * @description 过滤掉与仅加工孔槽板件匹配的余料
 * 当参与排版的板件满足 isOnlyProcessSlotAndHole 时，
 * 对应材质(type) 的余料也不应参与排版
 * @param surplusList 余料列表
 * @param plankList 参与排版的板件列表（需包含 matCode 字段）
 * @param processLine 当前生产线数据（isOnlyProcessSlotAndHole 所需参数）
 * @returns 过滤后的余料列表
 */
export function filterSurplusByNoCutPlanks<T extends { type: string }>(
  surplusList: T[],
  plankList: Array<{ matCode: string }>,
  processLine: any
): T[] {
  if (!processLine || !surplusList.length || !plankList.length) {
    return surplusList
  }

  // 收集满足仅加工孔槽条件的板件的 材质
  const noCutKeySet = new Set<string>()
  for (const plank of plankList) {
    if (isOnlyProcessSlotAndHole(processLine, plank.matCode)) {
      noCutKeySet.add(plank.matCode)
    }
  }

  if (!noCutKeySet.size) return surplusList

  return surplusList.filter((surplus) => {
    return !noCutKeySet.has(surplus.type)
  })
}

/**
 * 查找跨材质扩版计算所需的原始板件数据
 * 查找顺序：排版数据 → 预排版数据 → 缓存 → 自身（补件/待排版库场景）
 */
/** 跨材质扩展管线所需的上下文依赖 */
export interface CrossMaterialContext {
  /** 从缓存中获取板件原始渲染数据 */
  getCachedRawPlank: (partUniqueId: string) => any
}
export function getOriginalPartForCrossMaterialExpand(
  part: any,
  ctx: CrossMaterialContext
): any {
  const originId = part?.partUniqueId || part?.oriPartUniqueId
  const originPart = getOriPartByLayout(originId)
  if (!originPart) {
    if ((part._isBujian || part._isAwaitStorePlank) && part.partUniqueId) {
      const cached = ctx.getCachedRawPlank(part.partUniqueId)
      if (cached) return cached
    }
    throw new Error('未找到开料清单原始板件，无法扩版')
  }
  return originPart
}

/**
 * @description 判断是否是品牌小板（根据 colorId、brandName、supplierName）
 * @param part 小板数据
 * @returns 是否是品牌小板
 */
export function isBrandPart(part: any): boolean {
  // 判断是这三个字段是否有值
  return !!(part.colorId && part.brandName && part.supplierName)
}

/**
 * @description 判断是否是品牌大板（品牌大板不支持修改尺寸）
 * @param plank 大板数据
 * @returns 是否是品牌大板
 */
export function isBrandBigPlank(plank: any): boolean {
  return !!(plank && plank.colorId && plank.brandName && plank.supplierName)
}

/**
 * @description 校验小板是否允许拖动到目标大板（品牌色卡匹配）
 * @param part 小板数据
 * @param bigpart 目标大板数据
 * @returns 不允许时返回提示文案的 i18n key，允许返回 null
 */
export function getBrandDragErrorTipKey(
  part: any,
  bigpart: any
): string | null {
  const partIsBrand = isBrandPart(part)
  const bigpartIsBrand = isBrandBigPlank(bigpart)
  if (!partIsBrand && bigpartIsBrand) {
    return 'arrangedPage.nonBrandToBrandTip'
  }
  if (partIsBrand && !bigpartIsBrand) {
    return 'arrangedPage.brandToNonBrandTip'
  }
  if (
    partIsBrand &&
    bigpartIsBrand &&
    (part.colorId !== bigpart.colorId ||
      part.brandName !== bigpart.brandName ||
      part.supplierName !== bigpart.supplierName)
  ) {
    return 'arrangedPage.brandToOtherBrandTip'
  }
  return null
}

/**
 * @description 排版期间判断大板是否锁定（包含虚拟锁定）
 * @param {object} item - 大板对象
 * @param {boolean} [item.isLocked] - 是否真实锁定
 * @param {boolean} [item._isVirtualLock] - 是否虚拟锁定（按材质排版临时标记）
 * @returns {boolean} 是否处于锁定状态
 */
export function isPaibanLock(item: {
  isLocked?: boolean
  _isVirtualLock?: boolean
}): boolean {
  return !!(item.isLocked || item._isVirtualLock)
}

/**
 * @description 判断大板是否是复用父板
 * @param {object} board - 大板对象
 * @param {boolean} [board._isReused] - 是否已被复用
 * @returns {boolean} 是否是复用父板
 */
export function isReuseParent(board: { _isReused?: boolean }): boolean {
  return !!board._isReused
}

/**
 * @description 判断大板是否是复用子板
 * @param {object} board - 大板对象
 * @param {string} [board._reuseParentId] - 复用父板的 bigPlankUniqueId
 * @returns {boolean} 是否是复用子板
 */
export function isReuseChild(board: { _reuseParentId?: string }): boolean {
  return !!board._reuseParentId
}

/**
 * @description 判断大板是否是复用板（父板或子板）
 * @param {object} board - 大板对象
 * @returns {boolean} 是否是复用板
 */
export function isReuseBoard(board: {
  _isReused?: boolean
  _reuseParentId?: string
}): boolean {
  return isReuseParent(board) || isReuseChild(board)
}

/**
 * @description 清除指定大板的复用信息
 * @param {object} board - 大板对象
 * @param {object[]} [paibanData] - 可选，paibanData 数组（清除父板时需要清除子板标记）
 * @returns {void}
 */
export function clearReuseInfo(
  board: {
    _isReused?: boolean
    _reuseParentId?: string
    bigPlankUniqueId?: string | string[]
  },
  paibanData?: Array<{
    _reuseParentId?: string
    _isReused?: boolean
    bigPlankUniqueId?: string | string[]
  }>
): void {
  if (isReuseParent(board)) {
    delete board._isReused
    // 清除所有子板的标记
    if (paibanData) {
      const parentId = Array.isArray(board.bigPlankUniqueId)
        ? board.bigPlankUniqueId[0]
        : board.bigPlankUniqueId
      paibanData.forEach((p) => {
        const childParentId = Array.isArray(p._reuseParentId)
          ? p._reuseParentId[0]
          : p._reuseParentId
        if (childParentId === parentId) {
          delete p._reuseParentId
        }
      })
    }
  } else if (isReuseChild(board)) {
    const parentId = Array.isArray(board._reuseParentId)
      ? board._reuseParentId[0]
      : board._reuseParentId
    delete board._reuseParentId
    // 检查同组是否还有其他子板，如果没有则清除父板标记
    if (paibanData && parentId) {
      const hasOtherChild = paibanData.some((p) => {
        if (p === board) return false
        const pParentId = Array.isArray(p._reuseParentId)
          ? p._reuseParentId[0]
          : p._reuseParentId
        return pParentId === parentId
      })
      if (!hasOtherChild) {
        const parentBoard = paibanData.find((p) => {
          const pId = Array.isArray(p.bigPlankUniqueId)
            ? p.bigPlankUniqueId[0]
            : p.bigPlankUniqueId
          return pId === parentId && p._isReused
        })
        if (parentBoard) {
          delete parentBoard._isReused
        }
      }
    }
  }
}

/**
 * @description 比较两个异形轮廓路径是否完全一致
 * @param {PointType[][]} pathA - 轮廓路径 A
 * @param {PointType[][]} pathB - 轮廓路径 B
 * @param {number} [epsilon=0.0001] - 精度阈值
 * @returns {boolean} 是否一致
 */
function isPathEqual(
  pathA: PointType[][],
  pathB: PointType[][],
  epsilon = 0.0001
): boolean {
  if (pathA.length !== pathB.length) return false
  for (let i = 0; i < pathA.length; i++) {
    if (pathA[i].length !== pathB[i].length) return false
    const len = pathA[i].length
    // 先检查正向匹配
    let forwardMatch = true
    for (let j = 0; j < len; j++) {
      if (
        !compareNumbers(pathA[i][j].x, pathB[i][j].x, '=', epsilon) ||
        !compareNumbers(pathA[i][j].y, pathB[i][j].y, '=', epsilon)
      ) {
        forwardMatch = false
        break
      }
    }
    if (forwardMatch) continue
    // 正向不匹配时检查反向（逆序）匹配，轮廓方向相反仍视为同一形状
    let reverseMatch = true
    for (let j = 0; j < len; j++) {
      if (
        !compareNumbers(pathA[i][j].x, pathB[i][len - 1 - j].x, '=', epsilon) ||
        !compareNumbers(pathA[i][j].y, pathB[i][len - 1 - j].y, '=', epsilon)
      ) {
        reverseMatch = false
        break
      }
    }
    if (!reverseMatch) return false
  }
  return true
}

/**
 * @description 判断两个板件是否可以匹配（不含旋转/翻面变换）
 * @param {PartType} source - 源板件（父板上的小板）
 * @param {PartType} candidate - 候选板件（池中的小板）
 * @returns {boolean} 是否匹配
 */
function isPartMatchable(source: PartType, candidate: PartType): boolean {
  // 检查材质属性
  if (
    source.matCode !== candidate.matCode ||
    source.texture !== candidate.texture ||
    source.thick !== candidate.thick ||
    source.is_high_gloss_plank !== candidate.is_high_gloss_plank
  ) {
    return false
  }

  // 开启后匹配板件名称
  if (
    store.state.preferencesSetting?.setting?.isUsePlankReuseMatchingPlankName
  ) {
    if (source.partName !== candidate.partName) return false
  }

  // 检查纹理方向
  if (source.texDir !== 'notcare' && candidate.texDir !== 'notcare') {
    if (source.texDir !== candidate.texDir) return false
  }

  // 检查尺寸
  if (
    !compareNumbers(source.rect.width, candidate.rect.width) ||
    !compareNumbers(source.rect.height, candidate.rect.height)
  ) {
    return false
  }

  // 检查异形轮廓
  const sourceHasPath = source.path && source.path.length > 0
  const candidateHasPath = candidate.path && candidate.path.length > 0
  if (sourceHasPath && candidateHasPath) {
    if (!isPathEqual(source.path, candidate.path)) return false
  } else if (sourceHasPath !== candidateHasPath) {
    return false
  }

  return true
}

/**
 * @description 尝试通过旋转+翻面来匹配板件（支持无纹理和有纹理方向）
 * @param {PartType} source - 源板件（父板上的小板）
 * @param {PartType} candidate - 候选板件（池中的小板）
 * @returns {Object} 匹配结果和变换后的板件
 */
function tryMatchWithTransform(
  source: PartType,
  candidate: PartType
): { matched: boolean; transformed: PartType } {
  // 两边都有纹理且方向不同，不可能通过合法旋转匹配
  if (
    source.texDir !== 'notcare' &&
    candidate.texDir !== 'notcare' &&
    source.texDir !== candidate.texDir
  ) {
    return { matched: false, transformed: candidate }
  }
  // 检查材质属性
  if (
    source.matCode !== candidate.matCode ||
    source.texture !== candidate.texture ||
    source.thick !== candidate.thick ||
    source.is_high_gloss_plank !== candidate.is_high_gloss_plank
  ) {
    return { matched: false, transformed: candidate }
  }

  // 检查板件名称（名称不随旋转/翻面改变，提前过滤）
  if (
    store.state.preferencesSetting?.setting?.isUsePlankReuseMatchingPlankName &&
    source.partName !== candidate.partName
  ) {
    return { matched: false, transformed: candidate }
  }

  // 深拷贝候选板件，避免修改原始数据
  const copy = window.$copy(candidate)

  function matchCurrentStep(): {
    matched: boolean
    transformed: PartType
  } | null {
    if (isPartMatchable(source, copy)) {
      return { matched: true, transformed: copy }
    }
    return null
  }

  // 候选板有纹理：只能进行不改变纹理方向的 4 个状态（含翻面后 180°）
  if (candidate.texDir !== 'notcare') {
    const textureTransformSteps = [
      null,
      () => rotate180Part(copy),
      () => rolloverPlank(copy),
      () => rotate180Part(copy),
    ]
    for (const transform of textureTransformSteps) {
      transform?.()
      const result = matchCurrentStep()
      if (result) return result
    }
    return { matched: false, transformed: candidate }
  }
  // 候选板无纹理：覆盖 8 个状态，避免最后复原到初始状态的冗余操作
  const nonTextureTransformSteps = [
    null,
    () => rotatePart(copy),
    () => rotatePart(copy),
    () => rotatePart(copy),
    () => rolloverPlank(copy),
    () => rotatePart(copy),
    () => rotatePart(copy),
    () => rotatePart(copy),
  ]
  for (const transform of nonTextureTransformSteps) {
    transform?.()
    const result = matchCurrentStep()
    if (result) return result
  }

  return { matched: false, transformed: candidate }
}

/**
 * @description 从池中匹配所有可以填充父板布局的板件
 * @param {PartType[]} parentParts - 父板上的小板列表
 * @param {PartType[]} pool - 池中可用的小板列表
 * @returns {Object} 匹配结果
 */
function matchPartsForReuse(
  parentParts: PartType[],
  pool: PartType[]
): {
  matched: Array<{ source: PartType; replacement: PartType }>
  unusedPool: PartType[]
} {
  const remainingPool = [...pool]
  const matched: Array<{ source: PartType; replacement: PartType }> = []
  for (const source of parentParts) {
    let matchIndex = -1
    let replacement: PartType | null = null

    // 优先精确匹配
    matchIndex = remainingPool.findIndex((candidate) =>
      isPartMatchable(source, candidate)
    )
    if (matchIndex !== -1) {
      replacement = remainingPool[matchIndex]
    }
    // 再尝试旋转翻面匹配
    if (matchIndex === -1) {
      for (let i = 0; i < remainingPool.length; i++) {
        const result = tryMatchWithTransform(source, remainingPool[i])
        if (result.matched) {
          matchIndex = i
          replacement = result.transformed
          break
        }
      }
    }
    if (matchIndex !== -1 && replacement) {
      matched.push({ source, replacement })
      remainingPool.splice(matchIndex, 1)
    }
  }
  return { matched, unusedPool: remainingPool }
}

/**
 * @description 根据父板模板和匹配结果组装一个复用大板
 * @param {IBigPlank} parentBoard - 父板（模板）
 * @param {Array} matchedParts - 匹配到的板件对
 * @param {number} stockNum - 大板序号
 * @returns {IBigPlank} 组装好的复用大板
 */
function assembleReuseBoard(
  parentBoard: IBigPlank,
  matchedParts: Array<{ source: PartType; replacement: PartType }>,
  stockNum: number
): IBigPlank {
  // 浅拷贝父板，删除 parts 避免深拷贝大量数据
  const { parts: _parts, ...rest } = parentBoard
  const board = window.$copy(rest)

  // 生成新的 bigPlankUniqueId
  board.bigPlankUniqueId = nanoid()
  // 标记为复用子板，记录父板引用
  board._reuseParentId = parentBoard.bigPlankUniqueId
  // 子板不添加锁定
  board.isLocked = false

  // 设置 stockNum 和 stockKey
  board.stockNum = stockNum
  board.stockKey = `${parentBoard.texture}:${parentBoard.matCode}:${parentBoard.thick}:${stockNum}`

  // 用匹配到的板件替换 parts，只从源板件继承指定信息
  board.parts = matchedParts.map(({ source, replacement }) => {
    const part = window.$copy(replacement)

    // 从源板件继承：切割顺序、下刀点、位置
    part.priority = source.priority
    part.cutOrigin = source.cutOrigin
    part.startX = source.startX
    part.startY = source.startY

    // 无纹理信息（handleNonTexture 设置的）
    part.isNonTexture = source.isNonTexture
    part.mark_notcare = source.mark_notcare
    // 分层切割
    part.curve_layered_cutting = source.curve_layered_cutting
    part.curve_layered_cutting_deep = source.curve_layered_cutting_deep
    // 设置小板的 stockNum 和 stockKey
    part.stockNum = stockNum
    part.stockKey = board.stockKey
    // 处理板件轮廓点位
    dealPlankPoly(part)
    part.index = Date.now() + Math.floor(Math.random() * 100000)
    return part
  })

  return board
}

/**
 * @description 布局复用：以父板为模板，从同材质池中匹配板件组装复用大板
 * @param {IBigPlank} parentBoard - 父板（模板）
 * @param {PartType[]} pool - 池中可用的小板列表（同材质颜色厚度）
 * @param {number} startStockNum - 起始大板序号
 * @returns {Object} 组装结果
 */
export function layoutReuse(
  parentBoard: IBigPlank,
  pool: PartType[],
  startStockNum: number
): {
  reusedBoards: IBigPlank[]
  unusedParts: PartType[]
} {
  const reusedBoards: IBigPlank[] = []
  // 父板无可用模板板件时直接返回，避免空匹配导致 while 死循环
  if (!parentBoard.parts.length) {
    return { reusedBoards, unusedParts: [...pool] }
  }
  let remainingPool = [...pool]
  let currentStockNum = startStockNum
  while (remainingPool.length >= parentBoard.parts.length) {
    const result = matchPartsForReuse(parentBoard.parts, remainingPool)
    // 匹配数量不足，无法组装完整大板
    if (result.matched.length < parentBoard.parts.length) {
      break
    }
    const board = assembleReuseBoard(
      parentBoard,
      result.matched,
      currentStockNum
    )
    reusedBoards.push(board)
    remainingPool = result.unusedPool
    currentStockNum++
  }
  return { reusedBoards, unusedParts: remainingPool }
}

export interface ExpandRectInfo {
  origRect: { width: number; height: number }
  expandedX: number
  expandedY: number
  diameter: number
  contourSizeRecoup: number
  longPlankExpandedSize: number
  finalExtendDisMap: any
}

/**
 * 批量计算板件扩张并记录原始数据
 * - 非虚拟板：记录原始 rect → 扩张 rect（原地修改）
 * - 虚拟板：跳过
 * @returns map[partUniqueId] = ExpandRectInfo
 */
export function calcExpandRectMap(
  parts: PartType[],
  ncSetting: any
): Map<string, ExpandRectInfo> {
  const map = new Map<string, ExpandRectInfo>()

  for (const part of parts) {
    if (isVirtualCompositePlank(part)) continue
    const baRes = basisSettingExpandPlank(part, ncSetting)
    let { expandedX, expandedY, diameter } = baRes
    const { contourSizeRecoup, longPlankExpandedSize, finalExtendDisMap } =
      baRes
    diameter = +diameter
    if (part.path) {
      expandedX = longPlankExpandedSize
      expandedY = longPlankExpandedSize
    }
    const origRect = part.rect
    part.rect = {
      ...part.rect,
      width: +(
        origRect.width +
        expandedX * 2 +
        (finalExtendDisMap
          ? Number(finalExtendDisMap.left) + Number(finalExtendDisMap.right)
          : 0) +
        contourSizeRecoup * 2 +
        diameter
      ).toFixed(2),
      height: +(
        origRect.height +
        expandedY * 2 +
        (finalExtendDisMap
          ? Number(finalExtendDisMap.top) + Number(finalExtendDisMap.bottom)
          : 0) +
        contourSizeRecoup * 2 +
        diameter
      ).toFixed(2),
    }
    ;(part as any).blankWidth = Math.floor(part.rect.width * 100) / 100
    ;(part as any).blankHeight = Math.floor(part.rect.height * 100) / 100
    map.set(part.partUniqueId, {
      origRect,
      expandedX,
      expandedY,
      diameter,
      contourSizeRecoup,
      longPlankExpandedSize,
      finalExtendDisMap,
    })
  }
  return map
}
